import { Component, Input, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, finalize, first } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { AnnexService } from '../../../data/annex.service';
import { SnackBarService } from '../../../shared/services/snack-bar.service';
import { ErrorCode } from '../../../common/error-codes/ErrorCode';
import { SignatureMethodModalComponent, SignatureMethod } from './signature-method-modal/signature-method-modal.component';
import { SignatureStepDto } from 'src/app/models/dtos/annexes/signature-step-dto';
import { EmploymentAgreementDetailsDto } from 'src/app/models/dtos/employment-agreement-details-dto';
import { WorkerAgreementDetailsDto } from 'src/app/models/dtos/worker-agreement-details-dto';
import { WorkerAgreementForAnnexDto } from 'src/app/models/dtos/worker-agreements/worker-agreement-for-annex-dto';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { SplitComponentService } from 'src/app/split/services/split-component.service';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { EmploymentAgreementService } from 'src/app/data/employment-agreement.service';
import { AnnexPreviewService } from '../../services/annex-preview.service';
import { SplitComponentEnum } from 'src/app/common/enums/split-components';
import { AgreementAnnexSidePanelComponent } from '../agreement-annex-side-panel/agreement-annex-side-panel.component';
import { SignaturePolicyDto } from 'src/app/models/dtos/annexes/signature-policy-dto';
import { UserPermissionsDto } from 'src/app/models/dtos/annexes/user-permissions-dto';
import { CurrentSignatureDto } from 'src/app/models/dtos/annexes/current-signature-dto';
import { HttpErrorResponse } from '@angular/common/http';
import { AnnexDetailsDto } from 'src/app/models/dtos/annexes/annex-details-dto';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { FileService } from 'src/app/shared/services/file-service';
import { SignatureMethodDto } from 'src/app/models/dtos/annexes/signature-method-dto';

@Component({
  selector: 'app-annex-signature',
  templateUrl: './annex-signature.component.html',
  styleUrls: ['./annex-signature.component.scss'],
  providers: [AnnexPreviewService],
  standalone: false,
})
export class AnnexSignatureComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private splitComponentService = inject(SplitComponentService);
  private workerAgreementService = inject(WorkerAgreementService);
  private employmentAgreementService = inject(EmploymentAgreementService);
  private spinner = inject(NgxSpinnerService);
  private annexService = inject(AnnexService);
  private annexPreviewService = inject(AnnexPreviewService);
  private snackbarService = inject(SnackBarService);
  private authService = inject(AuthService);
  private fileService = inject(FileService);
  private dialog = inject(MatDialog);

  @ViewChild('stepper') stepper: MatStepper;

  @Input() annexId: number;
  @Input() workerId: number;
  @Input() employmentTypeId: number;
  @Input() agreementId: number;

  agreement: WorkerAgreementDetailsDto | EmploymentAgreementDetailsDto;
  dataForAnnex: WorkerAgreementForAnnexDto;
  splitComponentId = 'agreement-annex-signature-split';
  annexData: AnnexDetailsDto = null;
  signaturePolicies: SignaturePolicyDto[] = [];
  userPermissions: UserPermissionsDto = null;
  currentSignatures: CurrentSignatureDto[] = [];
  signatureSteps: SignatureStepDto[] = [];
  allowedMethods: SignatureMethodDto[] = [];
  previewError: boolean;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadAnnexSignatureData();
    this.loadAgreementDetails();
    this.showAnnexDocument();
  }

  ngOnDestroy(): void {
    this.splitComponentService.destroySplitter(this.splitComponentId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAnnexSignatureData(): void {
    this.spinner.show();
    this.previewError = false;
    this.annexService
      .getAnnexForSignature(this.annexId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: (data) => {
          this.annexData = data.Annex;
          this.signaturePolicies = data.SignaturePolicies || [];
          this.userPermissions = data.UserPermissions;
          this.currentSignatures = data.CurrentSignatures || [];
          this.allowedMethods = this.signaturePolicies.find((s) => s.SignatureTypeId === this.userPermissions?.SignatureTypeId)?.AllowedMethods;
          this.buildSignatureSteps();
        },
        error: (error: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(error.error);
          this.router.navigate(['/workers', this.agreement.WorkerId, 'employmentType', this.agreement.EmploymentTypeId, 'agreements', this.agreementId, 'annexes', this.annexId, 'details']);
        },
      });
  }

  showAgreementDetails(): void {
    const existingComponents = this.splitComponentService.getComponentsConfigs(this.splitComponentId);
    const detailsAlreadyShown = existingComponents?.some((component) => component.id === SplitComponentEnum.AgreementAnnexDetails);

    if (!detailsAlreadyShown) {
      this.splitComponentService.addComponent(this.splitComponentId, {
        id: SplitComponentEnum.AgreementAnnexDetails,
        componentType: AgreementAnnexSidePanelComponent,
        direction: 'horizontal',
        data: {
          agreement: this.agreement,
          splitId: this.splitComponentId,
          annexPreviewService: this.annexPreviewService,
        },
        size: 33,
        header: {
          closeButton: false,
        },
      });
    }
  }

  private loadAgreementDetails(): void {
      this.spinner.show();
  
      if (this.isEmploymentAgreement()) {
        forkJoin({
          agreement: this.employmentAgreementService.getEmploymentAgreementDetails(this.agreementId),
          dataForAnnex: this.workerAgreementService.getWorkerAgreementForAnnex(this.agreementId),
        })
          .pipe(
            first(),
            finalize(() => this.spinner.hide()),
          )
          .subscribe({
            next: ({ agreement, dataForAnnex }) => {
              this.agreement = agreement;
              this.dataForAnnex = dataForAnnex;
              this.showAgreementDetails();
            },
            error: () => {
              this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
            },
          });
      } else {
        forkJoin({
          agreement: this.workerAgreementService.getWorkerAgreementDetails(this.agreementId),
          dataForAnnex: this.workerAgreementService.getWorkerAgreementForAnnex(this.agreementId),
        })
          .pipe(
            first(),
            finalize(() => this.spinner.hide()),
          )
          .subscribe({
            next: ({ agreement, dataForAnnex }) => {
              this.agreement = agreement;
              this.dataForAnnex = dataForAnnex;
              this.showAgreementDetails();
            },
            error: () => {
              this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
            },
          });
      }
    }

  onSignClick(): void {
    const isEmployerRepresentative = this.userPermissions?.SignatureTypeId !== 1;

    if (this.allowedMethods.length === 0) {
      this.snackbarService.openErrorSnackBar(ErrorCode.AnnexSignatureConfigurationNotExist);
      return;
    }

    const dialogRef = this.dialog.open(SignatureMethodModalComponent, {
      disableClose: false,
      data: {
        annexId: this.annexId,
        workerId: this.workerId,
        isEmployerRepresentative: isEmployerRepresentative,
        allowedMethods: this.allowedMethods,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.router.navigate(['/workers', this.workerId, 'annexes', 'successfully-signed'], { queryParams: { annexId: this.annexId } });
      }
    });
  }

  get hasCurrentUserSigned(): boolean {
    const currentUserId = this.authService.authServerUserId;
    return this.currentSignatures.some((s) => s.SignerAuthServerUserId === currentUserId);
  }

  get canSign(): boolean {
    return this.userPermissions?.CanSign || false;
  }

  get isQualifiedSignature(): boolean {
    return this.userPermissions?.SignatureTypeId === 3; // SignatureMethod.Qualified
  }

  get isViewMode(): boolean {
    return !this.canSign;
  }

  private buildSignatureSteps(): void {
    this.signatureSteps = this.signaturePolicies.map((policy) => {
      const signedSignaturesNumber = this.currentSignatures.filter(
        (signature) => signature.SignatureTypeId === policy.SignatureTypeId
      ).length;

      const isSigned = signedSignaturesNumber >= policy.RequiredQuantity;

      return {
        id: policy.Id,
        label: policy.SignatureTypeName,
        state: isSigned ? 'done' : 'wait',
        order: policy.Order,
        requiredQuantity: policy.RequiredQuantity,
        signedQuantity: signedSignaturesNumber,
      } as SignatureStepDto;
    });
  }

  isStepSigned(signatureTypeId: number): boolean {
    return this.currentSignatures.some((signature) => signature.SignatureTypeId === signatureTypeId);
  }

  private showAnnexDocument(): void {
    this.annexService
      .showAnnexDocument(this.annexId)
      .pipe(
        finalize(() => {}),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          const blob = response.body;
          if (blob) {
            this.annexPreviewService.updatePreview(blob);
            this.previewError = false;
          }
        },
        error: (error: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(ErrorCode.AnnexPreviewGenerationError);
          this.previewError = true;
        },
      });
  }

  onDownloadAnnexClick(): void {
    this.annexService
      .showAnnexDocument(this.annexId)
      .pipe(first())
      .subscribe({
        next: (response) => {
          const blob = response.body;
          this.fileService.showFile(
            'aneks_do_umowy' +
              this.agreement.AgreementId +
              '_' + new Date().getTime() +
              '.pdf',
            null,
            null,
            blob,
          );
        },
        error: (error: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
        },
      });
  }

  private isEmploymentAgreement(): boolean {
    return this.employmentTypeId === EmploymentType.EmploymentAgreement || this.employmentTypeId === EmploymentType.TemporaryEmploymentAgreement;
  }

  private isWorkerAgreement(): boolean {
    return this.employmentTypeId === EmploymentType.MandateAgreement || this.employmentTypeId === EmploymentType.TemporaryMandateAgreement;
  }

  get workerAgreement(): WorkerAgreementDetailsDto | null {
    return this.isWorkerAgreement() ? (this.agreement as WorkerAgreementDetailsDto) : null;
  }

  get employmentAgreement(): EmploymentAgreementDetailsDto | null {
    return this.isEmploymentAgreement() ? (this.agreement as EmploymentAgreementDetailsDto) : null;
  }
}
